import React from 'react';
import type {
  BoxProps,
  BoxStyle,
  SvgRadialGradientProps,
  SvgTextProps,
  SvgCircleProps,
  SvgClipPathProps,
  SvgDefsProps,
  SvgGooeyProps,
  SvgGProps,
  SvgLineProps,
  SvgMaskProps,
  SvgPaintProps,
  SvgPathProps,
  SvgPatternProps,
  SvgProps,
  SvgRectProps,
} from './primitives.types';

/*
  DOM implementations of the shared render primitives.

  They are thin on purpose: BoxStyle is already a valid React.CSSProperties subset, so nothing here
  translates styles. The native siblings (View / react-native-svg) will be equally thin, which is the
  point — the shim exists to name a small vocabulary, not to reconcile two large ones.
*/

/**
 * React Native lays every View out as a column flex container; a DOM div is `display: block`. Without
 * this default, `flex: 1` silently does nothing on the web and identical style objects produce
 * different layouts — so the web Box adopts RN's defaults, and BoxStyle means one thing everywhere.
 */
const RN_BOX_DEFAULTS: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  // RN never lets a flex child shrink below its content the way the web does by default.
  minHeight: 0,
  minWidth: 0,
};

/**
 * A BoxStyle as the DOM wants it.
 *
 * Only `box-none` needs translating: the web has no such value, and `none` does the same job there
 * because a descendant that sets `auto` still receives its presses. Left as it is, the browser would
 * drop the declaration and the box would go on swallowing them.
 */
export const webStyle = (style?: BoxStyle): React.CSSProperties =>
  style?.pointerEvents === 'box-none'
    ? { ...(style as React.CSSProperties), pointerEvents: 'none' }
    : (style as React.CSSProperties);

export const Box: React.FC<BoxProps> = ({ style, children, testID }) => (
  <div style={{ ...RN_BOX_DEFAULTS, ...webStyle(style) }} data-testid={testID}>
    {children}
  </div>
);

export const Svg: React.FC<SvgProps> = ({ viewBox, preserveAspectRatio, style, children }) => (
  <svg viewBox={viewBox} preserveAspectRatio={preserveAspectRatio} style={style as React.CSSProperties}>
    {children}
  </svg>
);

/** The DOM wants the dash pattern as a string; react-native-svg takes the array as it is. */
export const SvgLine: React.FC<SvgLineProps> = ({ strokeDasharray, ...props }) => (
  <line {...props} strokeDasharray={strokeDasharray?.join(' ')} />
);

/**
 * The paint props in the spelling the DOM wants: the dash pattern as a string, and a clip path or
 * mask named by id turned into the `url(#id)` reference an SVG attribute takes.
 */
const paint = ({ strokeDasharray, clipPath, mask, ...rest }: SvgPaintProps) => ({
  ...rest,
  strokeDasharray: strokeDasharray?.join(' '),
  clipPath: clipPath && `url(#${clipPath})`,
  mask: mask && `url(#${mask})`,
});

export const SvgPath: React.FC<SvgPathProps> = ({ d, ...rest }) => <path d={d} {...paint(rest)} />;

export const SvgG: React.FC<SvgGProps> = ({ children }) => <g>{children}</g>;

export const SvgRect: React.FC<SvgRectProps> = ({ x, y, width, height, rx, ...rest }) => (
  <rect x={x} y={y} width={width} height={height} rx={rx} {...paint(rest)} />
);

export const SvgCircle: React.FC<SvgCircleProps> = ({ cx, cy, r, ...rest }) => (
  <circle cx={cx} cy={cy} r={r} {...paint(rest)} />
);

export const SvgDefs: React.FC<SvgDefsProps> = ({ children }) => <defs>{children}</defs>;

/** Declares a clip path for shapes to reference by id. Belongs inside `SvgDefs`. */
export const SvgClipPath: React.FC<SvgClipPathProps> = ({ id, children }) => <clipPath id={id}>{children}</clipPath>;

export const SvgMask: React.FC<SvgMaskProps> = ({ id, children }) => <mask id={id}>{children}</mask>;

export const SvgPattern: React.FC<SvgPatternProps> = ({ id, width, height, viewBox, children }) => (
  <pattern id={id} width={width} height={height} viewBox={viewBox} patternUnits="userSpaceOnUse">
    {children}
  </pattern>
);

export const SvgRadialGradient: React.FC<SvgRadialGradientProps> = ({ id, cx, cy, r, stops }) => (
  <radialGradient id={id} cx={cx} cy={cy} r={r} gradientUnits="userSpaceOnUse">
    {stops.map((stop, index) => (
      <stop key={index} offset={stop.offset} stopColor={stop.color} stopOpacity={stop.opacity} />
    ))}
  </radialGradient>
);

export const SvgText: React.FC<SvgTextProps> = ({
  x,
  y,
  fontSize,
  fontFamily,
  fontWeight,
  fill,
  opacity,
  stroke,
  strokeWidth,
  strokeLinejoin,
  children,
}) => (
  <text
    x={x}
    y={y}
    fontSize={fontSize}
    fontFamily={fontFamily}
    fontWeight={fontWeight}
    fill={fill}
    opacity={opacity}
    stroke={stroke}
    strokeWidth={strokeWidth}
    strokeLinejoin={strokeLinejoin}
    // Centred on the anchor in both renderers; RN's Svg text takes the same two attributes.
    textAnchor="middle"
    dominantBaseline="middle"
  >
    {children}
  </text>
);

/*
  Blur strength, in board units. Shared with iOS, which spreads a blur about as far as the browser
  does; Android needs several times this. See SvgGooeyProps.
*/
const GOOEY_BLUR = 5;

/** The alpha ramp that turns a blur back into a hard edge: steepen alpha, then cut it at half. */
const GOOEY_THRESHOLD = '1 0 0 0 0  0 1 0 0 0  0 0 1 0 0  0 0 0 60 -30';

export const SvgGooey: React.FC<SvgGooeyProps> = ({ id, fill, x = 0, y = 0, width, height, children }) => (
  <>
    <defs>
      {/* Room to blur into: a shape at the edge of its own box would otherwise lose its rounding. */}
      <filter id={id} x="-50%" y="-50%" width="200%" height="200%" colorInterpolationFilters="sRGB">
        <feGaussianBlur in="SourceGraphic" stdDeviation={GOOEY_BLUR} result="blur" />
        <feColorMatrix in="blur" type="matrix" values={GOOEY_THRESHOLD} />
      </filter>
      <mask id={`${id}-mask`}>
        <g filter={`url(#${id})`}>{children}</g>
      </mask>
    </defs>
    <rect x={x} y={y} width={width} height={height} fill={fill} mask={`url(#${id}-mask)`} />
  </>
);
